<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { findFallbackIcon, iconFallbackSizeConfig } from '.'

const props = withDefaults(
  defineProps<{
    class?: HTMLAttributes['class']
    type?: 'image' | 'file' | 'video' | 'audio' | 'contact' | 'location'
    src?: string
    label?: string
    dismissable?: boolean
    iconFallbackSize?: keyof typeof iconFallbackSizeConfig
    invalid?: boolean
    invalidMessage?: string | null
  }>(),
  {
    class: undefined,
    type: 'image',
    src: undefined,
    label: undefined,
    iconFallbackSize: 'xs',
    dismissable: true,
    invalidMessage: undefined
  }
)

const emits = defineEmits<{
  (e: 'dismiss'): void
}>()

const handleDismiss = (e: MouseEvent) => {
  e.stopPropagation()
  emits('dismiss')
}

const makeIcon = computed(() => findFallbackIcon(props.type))
</script>

<template>
  <div
    :class="
      cn(
        'bg-surface-container-low p-3 rounded-xl flex flex-col gap-2',
        props.class
      )
    "
  >
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-2 justify-start overflow-hidden">
        <Icon
          :name="makeIcon"
          class="text-outline flex-none"
          :size="iconFallbackSizeConfig[iconFallbackSize]"
        />
        <span
          class="text-xs font-semibold line-clamp-1"
          :class="{ 'text-error': props.invalid }"
          >{{ props.label }}</span
        >
      </div>
      <Icon
        v-if="props.dismissable"
        name="material-symbols:close"
        class="text-primary cursor-pointer flex-none"
        :size="iconFallbackSizeConfig[iconFallbackSize]"
        @click="handleDismiss"
      />
    </div>
    <p v-if="props.invalid" class="text-[8px] font-medium text-error">
      {{ props.invalidMessage }}
    </p>
  </div>
</template>
